import { useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { useT, useLabels } from "../i18n";
import { RESULT_FORMATS, formatSeconds, parseHms } from "../constants/rox";

const EMPTY = { city: "", year: new Date().getFullYear(), format: "doubles_mixed", time: "" };

export const ResultsList = ({ results = [], compact = false }) => {
  const L = useLabels();
  if (!results.length) return null;
  return (
    <div className={`grid gap-2 ${compact ? "" : "sm:grid-cols-2"}`} data-testid="results-list">
      {results.map((r, i) => (
        <div key={i} className="panel flex items-center justify-between gap-3 p-3" data-testid={`result-item-${i}`}>
          <div className="min-w-0">
            <p className="h-display text-lg">HYROX {r.city} <span className="text-rox-orange">— {r.year}</span></p>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-rox-muted">{L.format(r.format)}</p>
          </div>
          <p className="h-display shrink-0 text-2xl text-rox-off">{formatSeconds(r.time_seconds)}</p>
        </div>
      ))}
    </div>
  );
};

export const ResultsEditor = ({ results = [], onSave, busy }) => {
  const { t } = useT();
  const L = useLabels();
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(EMPTY);
  const [error, setError] = useState("");

  const start = (i) => {
    setError("");
    setEditing(i);
    setDraft(i === "new" ? EMPTY : { city: results[i].city, year: results[i].year, format: results[i].format, time: formatSeconds(results[i].time_seconds) });
  };
  const submit = async (e) => {
    e.preventDefault();
    const secs = parseHms(draft.time);
    if (!secs || secs < 1200 || secs > 14400) return setError(t("results_time"));
    const item = { city: draft.city.trim(), year: Number(draft.year), format: draft.format, time_seconds: secs };
    const next = editing === "new" ? [...results, item] : results.map((r, i) => (i === editing ? item : r));
    await onSave(next);
    setEditing(null);
  };
  const remove = (i) => onSave(results.filter((_, k) => k !== i));

  return (
    <section className="mt-8" data-testid="results-section">
      <h2 className="h-display text-3xl">{t("results_title")}</h2>
      <p className="mt-1 text-xs text-rox-muted">{t("results_sub")}</p>
      <div className="mt-4 space-y-2">
        {results.length === 0 && editing === null && <p className="text-sm text-rox-muted" data-testid="results-empty">{t("results_empty")}</p>}
        {results.map((r, i) => (
          <div key={i} className="panel flex items-center gap-3 p-3" data-testid={`result-row-${i}`}>
            <div className="min-w-0 flex-1">
              <p className="h-display text-lg">HYROX {r.city} <span className="text-rox-orange">— {r.year}</span></p>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-rox-muted">{L.format(r.format)}</p>
            </div>
            <p className="h-display text-2xl">{formatSeconds(r.time_seconds)}</p>
            <button data-testid={`result-edit-${i}`} onClick={() => start(i)} className="text-rox-muted hover:text-rox-orange" aria-label={t("results_edit")}><Pencil size={15} /></button>
            <button data-testid={`result-delete-${i}`} onClick={() => remove(i)} className="text-rox-muted hover:text-rox-orange" aria-label={t("results_delete")}><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
      {editing !== null ? (
        <form onSubmit={submit} className="panel mt-3 grid gap-3 p-4 sm:grid-cols-2" data-testid="result-form">
          <input data-testid="result-city" className="field" placeholder={t("results_city")} value={draft.city} onChange={(e) => setDraft({ ...draft, city: e.target.value })} required maxLength={60} />
          <input data-testid="result-year" className="field" type="number" min={2017} max={new Date().getFullYear()} placeholder={t("results_year")} value={draft.year} onChange={(e) => setDraft({ ...draft, year: e.target.value })} required />
          <select data-testid="result-format" className="field appearance-none" value={draft.format} onChange={(e) => setDraft({ ...draft, format: e.target.value })}>
            {RESULT_FORMATS.map((f) => <option key={f} value={f}>{L.format(f)}</option>)}
          </select>
          <input data-testid="result-time" className="field" placeholder={t("results_time")} value={draft.time} onChange={(e) => setDraft({ ...draft, time: e.target.value })} required pattern="^\d{1,2}:\d{2}(:\d{2})?$" />
          {error && <p className="text-sm text-rox-orange sm:col-span-2" data-testid="result-error">{error}</p>}
          <div className="flex gap-2 sm:col-span-2">
            <button type="button" data-testid="result-cancel" onClick={() => setEditing(null)} className="btn-ghost flex-1 py-3 text-sm">{t("cancel")}</button>
            <button data-testid="result-save" disabled={busy} className="btn-primary flex-1 py-3 text-sm">{t("results_save")}</button>
          </div>
        </form>
      ) : (
        <button data-testid="result-add" onClick={() => start("new")} className="mt-3 flex w-full items-center justify-between rounded-[4px] border border-rox-line px-4 py-3 font-display text-sm uppercase tracking-wider hover:border-rox-orange hover:text-rox-orange">
          {t("results_add")} <Plus size={16} />
        </button>
      )}
    </section>
  );
};
